import API_CONFIG from "../config/api";
const API_BASE_URL = API_CONFIG.BASE_URL + '/auth';

export const loginUser = async (username, password) => {
  const formDetails = new URLSearchParams();
  formDetails.append('username', username);
  formDetails.append('password', password);

  const response = await fetch(`${API_BASE_URL}/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    credentials: 'include', 
    body: formDetails,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Authentication failed!');
  }
  return response.json(); // { access_token: "...", token_type: "bearer" }
};

export const registerUser = async (username, email, password) => {
  // Assuming your backend registration endpoint is /auth/register
  // and it expects a JSON payload. Adjust if your backend is different.
  const response = await fetch(`${API_BASE_URL}/register`, { // Or `/users/`, etc.
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json();

    let errorMessage = 'Registration failed due to an unknown error.'; // Default

    if (errorData && errorData.detail) {
      if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
        // This is a Pydantic validation error (422)
        // We'll take the message from the first error detail object.
        // You could also concatenate them if you prefer.
        errorMessage = errorData.detail[0].msg;
        // Optionally, you can include the field:
        // const field = errorData.detail[0].loc[errorData.detail[0].loc.length - 1];
        // errorMessage = `${field}: ${errorData.detail[0].msg}`;
      } else if (typeof errorData.detail === 'string') {
        // This is an error like 400, 401, 403 where 'detail' is a string
        errorMessage = errorData.detail;
      }
    } else if (errorData && errorData.message && typeof errorData.message === 'string') {
      // Fallback for other error structures that might use a 'message' field
      errorMessage = errorData.message;
    }
    
    // Now, errorMessage is guaranteed to be a string
    throw new Error(errorMessage);
  }
  // If registration is successful, the backend might return the created user object or a success message.
  // For the purpose of just registering and then logging in, we might not strictly need the response data here,
  // but it's good practice to return it.
  return response.json(); 
};

export const verifyToken = async (token) => {
  // Note: Sending token in URL is generally not recommended for sensitive operations.
  // Prefer sending it in an Authorization header if your backend supports it.
  // GET /auth/verify-token with Authorization: Bearer <token>
  // For this example, we stick to your existing backend structure.
  const response = await fetch(`${API_BASE_URL}/verify-token/${token}`);
  if (!response.ok) {
    throw new Error('Token verification failed');
  }
  return response.json(); // Or just response.ok if it doesn't return data
};

export const refreshToken = async () => {
  const response = await fetch(`${API_BASE_URL}/refresh`, {
    method: 'GET',
    // IMPORTANT: This tells the browser to send cookies along with the request.
    // This is necessary for the httpOnly refresh_token to be sent to the backend.
    credentials: 'include', 
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to refresh token.');
  }
  return response.json(); // { access_token: "...", token_type: "bearer" }
};

/**
 * Calls the backend to log the user out, which will clear the httpOnly cookie.
 */
export const logoutUser = async () => {
  const response = await fetch(`${API_BASE_URL}/logout`, {
    method: 'GET',
    // IMPORTANT: Also need credentials here to send the cookie that needs to be deleted.
    credentials: 'include',
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Logout failed.');
  }
  return response.json(); // { message: "Logged out successfully" }
};